<template>
    <div>
        <v-card class="user-roles" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('user_roles.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="!curRole"
                            class="minw100"
                            color="white"
                            outlined
                            @click="addRole"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text class="card-body">
                <v-window :value="!curRole ? 1 : 2" touchless>
                    <v-window-item :value="1">
                        <v-data-table
                            :headers="headers"
                            :items="roles"
                            :loading="loading"
                            :mobile-breakpoint="0"
                            hide-default-footer
                            disable-sort
                            class="operations striped"
                        >
                            <template slot="item" slot-scope="props">
                                <tr>
                                    <td class="w150 maxw150">{{ props.item.name }}</td>
                                    <td align="center" class="w35 maxw60">
                                        <v-menu bottom transition="slide-y-transition">
                                            <template v-slot:activator="{ on }">
                                                <v-btn v-on="on" icon>
                                                    <v-icon>far fa-ellipsis-h</v-icon>
                                                </v-btn>
                                            </template>

                                            <v-list>

                                                <v-list-item
                                                    :key="1"
                                                    @click="copyRole(props.item)"
                                                >
                                                    <v-list-item-action class="mr-4 flex justify-content-center">
                                                        <v-icon>fal fa-copy</v-icon>
                                                    </v-list-item-action>
                                                    <v-list-item-title>
                                                        {{ $t('general.copy') }}
                                                    </v-list-item-title>
                                                </v-list-item>

                                                <template v-if="props.item.id > 2">
                                                    <v-divider />

                                                    <v-list-item
                                                        :key="2"
                                                        @click="openForm(props.item)"
                                                    >
                                                        <v-list-item-action class="mr-4 flex justify-content-center">
                                                            <v-icon>fal fa-pen</v-icon>
                                                        </v-list-item-action>
                                                        <v-list-item-title>
                                                            {{ $t('general.edit') }}
                                                        </v-list-item-title>
                                                    </v-list-item>

                                                    <v-divider />

                                                    <v-list-item
                                                        :key="3"
                                                        @click="deleteRole(props.item)"
                                                    >
                                                        <v-list-item-action class="mr-4 flex justify-content-center">
                                                            <v-icon class="error--text">fal fa-times</v-icon>
                                                        </v-list-item-action>
                                                        <v-list-item-title class="error--text">
                                                            {{ $t('general.delete') }}
                                                        </v-list-item-title>
                                                    </v-list-item>
                                                </template>
                                            </v-list>
                                        </v-menu>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-window-item>

                    <v-window-item :value="2">
                        <v-form ref="form" class="wfull d-flex">
                            <v-text-field
                                v-if="!form.id"
                                ref="nameField"
                                v-model="form.name"
                                :label="$t('general.name')"
                                :rules="form.rules.name"
                                autofocus
                                outlined
                                validate-on-blur
                                onfocus="this.select()"
                            />

                            <template v-else-if="!renameActive">
                                <v-select
                                    v-model="curRole"
                                    item-text="name"
                                    :items="roles"
                                    outlined
                                    return-object
                                    @change="openForm(curRole)"
                                />

                                <v-btn
                                    class="pa-2 mx-1 mt-2"
                                    icon
                                    @click="renameActive = true"
                                >
                                    <v-icon>fal fa-edit</v-icon>
                                </v-btn>
                            </template>

                            <template v-else>
                                <v-text-field
                                    ref="nameField"
                                    v-model="form.name"
                                    :label="$t('general.name')"
                                    :rules="form.rules.name"
                                    autofocus
                                    outlined
                                    validate-on-blur
                                    append-icon="fal fa-times"
                                    onfocus="this.select()"
                                    @keydown.enter="save"
                                    @click:append="renameActive = false; form.name = curRole.name"
                                />

                                <v-btn
                                    class="pa-2 mx-1 mt-2"
                                    icon
                                    @click="renameActive = false; curRole.name = form.name"
                                >
                                    <v-icon>fal fa-check</v-icon>
                                </v-btn>
                            </template>
                        </v-form>

                        <v-expansion-panels accordion focusable>
                            <v-expansion-panel
                                v-for="(category, index) in permissionCategories"
                                :key="index"
                                class="permission-category"
                            >
                                <v-expansion-panel-header>
                                    {{ $t(`user_roles.category.${category}`) }}
                                </v-expansion-panel-header>

                                <v-expansion-panel-content class="pt-2">
                                    <div
                                        v-for="permission in permissionsByCategory(category)"
                                        :key="permission.id"
                                    >
                                        <div
                                            v-if="labels[permission.name]"
                                            class="error--text mt-4 mb-0"
                                        >
                                            {{ labels[permission.name] }}
                                        </div>
                                        <v-checkbox
                                            v-model="permission.selected"
                                            :label="getPermissionLabel(permission)"
                                            class="my-1"
                                            hide-details
                                            :disabled="!permission.enabled"
                                            @change="onChange(permission, $event)"
                                        />
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-window-item>
                </v-window>
            </v-card-text>

            <v-spacer />

            <v-card-actions
                v-if="curRole"
                class="card-actions"
            >
                <v-btn
                    color="primary"
                    class="flex-1 mr-1"
                    depressed
                    outlined
                    @click="curRole = null"
                >
                    {{ $t('general.back') }}
                </v-btn>
                <v-btn
                    color="primary"
                    class="flex-1 ml-1"
                    depressed
                    :loading="form.busy"
                    :disabled="form.busy || renameActive"
                    @click="save"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import EinvoiceService from '@/services/EinvoiceService';

export default {
    created() {
        loadData('/admin/load/roles').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            roles: [],
            permissions: [],
            permissionCategories: [],

            form: this.createForm(),
            curRole: null,

            renameActive: false,
            loading: false,

            headers: [
                { text: this.$t('general.name'), value: 'name' },
                { text: '' }
            ],

            labels: this.$t('user_roles.labels'),
        };
    },

    watch: {
        curRole(val) {
            this.permissions = this.permissions.map(permission => {
                const isSelected = !this.curRole
                    ? false
                    : this.curRole.permissions.some(p => p.id === permission.id)
                return { ...permission, selected: isSelected, enabled: true }
            })

            this.curatePermissions()
        },
    },

    methods: {
        /**
         * On Data loaded
         */
        onDataLoaded(data) {
            this.roles = data.roles.map(role => {
                // if default role and name translation exists use it
                const transKey = `user_roles.default.${role.name}`
                if(this.$t(transKey) !== transKey && role.id <= 4) {
                    role.name = this.$t(transKey)
                }

                return role
            })

            // Filter user permissions
            const inventoryAccountEnabled = !!this.tenant.venue?.settings?.use_inventory_accounting;

            this.permissions = data.permissions.filter(p => {
                if (p.hidden) return false

                if(!this.isAllegra) {
                    return p.app !== 'Allegra'
                } else {
                    return !p.app || p.app === 'Allegra'
                }
            }).filter(p => {
                if(['access_receptions', 'access_dispatches'].includes(p.name) && inventoryAccountEnabled && isAllegra) return false;
                if(['access_client_transactions'].includes(p.name) && !inventoryAccountEnabled && !EinvoiceService.isEnabled()) return false;
                if([
                    'access_beginning_inventories',
                    'access_price_calculations',
                    'access_price_levelings',
                    'access_write_offs',
                    'access_cash_sales',
                    'access_stock',
                    'access_sales_purchase_ledger',
                ].includes(p.name) && !inventoryAccountEnabled) return false;

                return true;
            })

            this.permissionCategories = this.permissions
                .map(p => p.category)
                .filter((item, index, array) => array.indexOf(item) === index)
        },

        /**
         * Create Role form
         */
        createForm(role = null) {
            return new CodForm({
                id: role ? role.id : null,
                name: role ? role.name : null,
                permissions: [],

                rules: {
                    name: [
                        CodForm.rule('unique', () => {
                            return this.roles
                                .filter(r => this.curRole && r.name !== this.curRole.name)
                                .map(r => r.name)
                        })
                    ]
                }
            });
        },

        /**
         * Open Role form
         */
        openForm(role = null) {
            this.curRole = role;
            this.form = this.createForm(role);
        },

        /**
         * Save the role
         */
        save() {
            if(!this.$refs.form.validate()) return

            this.form.permissions = this.permissions.filter(p => p.selected).map(p => p.id)

            if (this.form.id) {
                ApiService.patch(`/admin/roles/${this.form.id}`, this.form).then(response => {
                    this.roles.forEach((item, index, array) => {
                        if (item.id === this.form.id) {
                            this.$set(array, index, response.data);
                        }
                    });

                    this.curRole = null
                    this.$snackbar(this.$t('user_roles.updated'), 'success');
                });
            } else {
                ApiService.post('/admin/roles', this.form).then(response => {
                    this.roles.push(response.data);
                    this.curRole = null
                    this.$snackbar(this.$t('user_roles.added'), 'success');
                });
            }
        },

        /**
         * Add new role by copying the admin role
         */
        addRole() {
            const adminRole = this.roles.find(role => role.id === 2)
            if(adminRole) {
                this.copyRole({ ...adminRole, name: '' })
            }
        },

        /**
         * Create a new role by copying the existing one
         */
        copyRole(role) {
            let roleCopy = _.cloneDeep(role)
            if(roleCopy.name) {
                roleCopy.name += ' copy'
            }
            roleCopy.id = null

            this.openForm(roleCopy)

            setTimeout(() => {
                this.$refs.nameField.focus()
            }, 100);
        },

        /**
         * Delete Role (if not Admin or Super Admin )
         */
        deleteRole(role) {
            if (role.id <= 2) {
                return;
            }

            this.$confirm(
                this.$t('user_roles.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/roles/${role.id}`, {})
                        .then(response => {
                            this.roles = this.roles.filter(item => item.id !== role.id);
                            this.$snackbar(this.$t('user_roles.deleted'), 'success');
                        })
                        .catch(error => {
                            if (error.response.status === 400) {
                                error.response.data.errors.forEach(item => {
                                    if (item.code === 'has_user') {
                                        this.$snackbar(this.$t('user_roles.has_user'), 'error');
                                    }
                                });
                            }
                        });
                }
            );
        },

        /**
         * Get permission in the specific category
         */
        permissionsByCategory(category) {
            return this.permissions.filter(p => p.category === category)
        },

        /**
         * Get the value of the permission by name
         */
        getPermissionValue(name) {
            const permission = this.permissions.find(p => p.name === name)
            return permission && permission.selected
        },

        /**
         * Enable / Disable permissions
         */
        enablePermissions(names, enabled) {
            if (!Array.isArray(names)) {
                names = [names];
            }

            const permissions = this.permissions.filter(p => names.includes(p.name))
            for(let i = 0; i < permissions.length; i++) {
                permissions[i].enabled = enabled
            }
        },

        /**
         * On permission change make sure dependent
         * permissions are selected, disabled, etc.
         */
        onChange(permission, event) {
            if(permission.category !== 'navigation') {
                return
            }

            // enable / disable permissions according to allowed access
            const accessPermissions = this.permissions.filter(p => p.name.includes('nav_access_')).map(p => p.name)
            const navDefaultPermissions = this.permissions.filter(p => p.name.includes('nav_') && p.name.includes('_default')).map(p => p.name)
            const orderIssuedPermissions = this.permissions.filter(p => p.name.includes('nav_order_issued')).map(p => p.name)
            const orderBackPermissions = this.permissions.filter(p => p.name.includes('nav_order_back')).map(p => p.name)

            this.$nextTick(() => {
                // make sure access to at least one section is allowed
                if(accessPermissions.includes(permission.name)) {
                    this.setSelected(permission, accessPermissions)
                }

                // enable / disable permissions according to allowed access
                this.curatePermissions()

                // Make sure we have none or exactly one permission selected in every role
                this.setSelected(permission, navDefaultPermissions, 1)

                const accessOrder = this.getPermissionValue('nav_access_order')
                this.setSelected(permission, orderIssuedPermissions, accessOrder ? 1 : 0)
                this.setSelected(permission, orderBackPermissions, accessOrder ? 1 : 0)
            })
        },

        /**
         * Make sure we have none, exactly one, or at least one permission selected
         */
        setSelected(permission, permissionGroup, maxSelected = 3) {
            const permissions = this.permissions.filter(p => permissionGroup.includes(p.name))
            const selectedCount = permissions.filter(p => p.selected).length

            if(maxSelected === 0) {
                for(let i = 0; i < permissions.length; i++) {
                    permissions[i].selected = false
                }
            }

            if(maxSelected === 1 && selectedCount > 1) {
                const leaveSelected = permissionGroup.includes(permission.name)
                    ? permission
                    : permissions.find(p => p.selected)

                const otherPermissions = permissions.filter(p => p.name !== leaveSelected.name)
                for(let i = 0; i < otherPermissions.length; i++) {
                    otherPermissions[i].selected = false
                }
            }

            if(maxSelected >= 1 && selectedCount === 0) {
                if(permissionGroup.includes(permission.name)) {
                    permission.selected = true
                } else {
                    for(let i = 0; i < permissions.length; i++) {
                        if(permissions[i].enabled) {
                            permissions[i].selected = true
                            break
                        }
                    }
                }
            }
        },

        /**
         * Disable permissions according to access restriction
         */
        curatePermissions() {
            const accessAdmin = this.getPermissionValue('nav_access_admin')
            const accessTables = this.getPermissionValue('nav_access_tables')
            const accessOrder = this.getPermissionValue('nav_access_order')

            this.enablePermissions('nav_admin_default', accessAdmin)
            this.enablePermissions('nav_tables_default', accessTables)
            this.enablePermissions('nav_order_default', accessOrder)

            this.enablePermissions(
                ['nav_order_issued_return_to_tables', 'nav_order_back_return_to_tables', 'nav_order_back_logout'],
                accessTables && accessOrder
            )

            this.enablePermissions(
                ['nav_order_issued_stay_on_order', 'nav_order_issued_logout', 'nav_order_back_logout'],
                accessOrder
            )

            this.enablePermissions(
                this.permissions.filter(p => p.category === 'access').map(p => p.name),
                accessAdmin
            )

            // unselect disabled permissions
            for(let i = 0; i < this.permissions.length; i++) {
                if(!this.permissions[i].enabled) {
                    this.permissions[i].selected = false
                }
            }
        },

        /**
         * Get the permission label
         */
        getPermissionLabel(permission) {
            if(permission.category === 'access') {
                const adminPage = permission.name.replace('access_', '')
                return this.$t(`admin_menu.item.${adminPage}`)
            }
            return this.$t(`user_roles.permission.${permission.name}`)
        }
    },
}
</script>

<style lang="scss" scoped>
.user-roles {
    max-width: 600px;
    min-height: 450px;
    display: flex;
    flex-direction: column;

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .card-body {
        margin-top: 16px;

        .permission-category:before {
            box-shadow: none;
        }
    }

    .card-actions {
        padding: 16px;
    }
}
</style>
